import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { ApiAuthenticationV1Service, MepStorage } from '@meplis/services';
import {
  AuthenticationHelper,
  LoginCredentials,
  LoginResult,
} from '@meplis/common';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  public onLoggedInOut = new Subject<void>();
  private _loggedIn: boolean;

  public token: string;

  constructor(
    private _apiAuthenticationV1Service: ApiAuthenticationV1Service,
    private _mepStorage: MepStorage,
    private _router: Router,
    private _activatedRoute: ActivatedRoute
  ) {}

  public async verifyIfIsLoggedIn(routeToNavigate?: string): Promise<void> {
    if (!(await this._hasRefreshToken())) {
      this.logOut(routeToNavigate);
      return;
    }

    if (await this._isExpiredToken()) {
      try {
        await this._refreshToken();
      } catch (error) {
        this.logOut(routeToNavigate);
        return;
      }
    }

    this.token = await this._mepStorage.get('access_token');
    this.setLoggedIn(true);
  }

  public async login(
    credentials: LoginCredentials,
    routeToNavigate?: string
  ): Promise<boolean> {
    const loginResult =
      await this._apiAuthenticationV1Service.loginWithPassword(credentials);
    this._setSession(loginResult);
    this.setLoggedIn(true);

    if (routeToNavigate) {
      this._navigateTo(routeToNavigate);
    } else {
      await this._redirectRoute();
    }

    if (!AuthenticationHelper.verifyTokenIntegrity(loginResult.access_token)) {
      this.logOut('login');
      return false;
    }

    return true;
  }

  private async _hasRefreshToken(): Promise<boolean> {
    const refreshToken = await this._getRefreshToken();

    if (refreshToken && refreshToken.length > 0) {
      return true;
    }

    return false;
  }

  public setLoggedIn(isLogged: boolean): void {
    this._loggedIn = isLogged;
    this.onLoggedInOut.next();
  }

  public get isLogged(): boolean {
    return this._loggedIn;
  }

  public getLoggedInObservable(): Observable<void> {
    return this.onLoggedInOut.asObservable();
  }

  private async _isExpiredToken(): Promise<boolean> {
    const endDateExpiration = await this._mepStorage.get('end_date');
    if (!endDateExpiration) {
      return true;
    }

    const start = new Date().getTime();
    const end = new Date(endDateExpiration as any).getTime();

    return start > end;
  }

  private async _getRefreshToken(): Promise<string> {
    return await this._mepStorage.get('refresh_token');
  }

  public getToken(): string {
    return this.token;
  }

  private async _refreshToken(): Promise<void> {
    const refreshToken = this._getRefreshToken();

    if (!refreshToken) {
      return;
    }

    const refreshTokenResult =
      await this._apiAuthenticationV1Service.loginRefreshToken(
        refreshToken as any
      );
    this._setSession(refreshTokenResult);
  }

  private async _setSession(loginResult: LoginResult): Promise<void> {
    loginResult.end_date = new Date(
      new Date().getTime() + Number(loginResult.expires_in) * 1000
    ).toISOString();

    this._mepStorage.setItems(loginResult);
    this.token = loginResult.access_token;
  }

  public async logOut(
    routeToNavigate?: string,
    removeSession = true
  ): Promise<boolean> {
    if (this._router.url === '/') {
      return false;
    }

    // Even if the API errors, we still want to remove the session
    try {
      const token = (await this.getToken()) as string;
      if (token && removeSession) {
        await this._apiAuthenticationV1Service.logout(token);
      }
    } catch (error) {
      // mleon(): implement LogReportService
      console.log(error);
      return false;
    }

    this._removeSession(routeToNavigate);
    return true;
  }

  private _redirectRoute(): Promise<void> {
    const queryParams = this._activatedRoute.snapshot.queryParams;
    const routeUrlToRedirect = queryParams['routeUrlToRedirect']?.split('/');
    const routeUrlToRedirectString = queryParams['routeUrlToRedirect'];

    const extrasQueryParams = Object.keys(queryParams)
      .filter((key) => key !== 'routeUrlToRedirect')
      .reduce((obj: any, key: string) => {
        obj[key] = queryParams[key];
        return obj;
      }, {});

    const extras: NavigationExtras = {
      queryParams: {
        ...extrasQueryParams,
      },
    };

    if (routeUrlToRedirectString) {
      this._router.navigateByUrl(routeUrlToRedirectString);
      return;
    }

    if (routeUrlToRedirect) {
      this._router.navigate(routeUrlToRedirect, extras);
      return;
    }

    this._router.navigate([''], { replaceUrl: true });
  }

  private async _removeSession(routeToNavigate?: string): Promise<void> {
    const loginResult = new LoginResult();
    const keys = Object.keys(loginResult);

    this._mepStorage.removeItems(keys);

    this.setLoggedIn(false);
    this._navigateTo(routeToNavigate);
  }

  private _navigateTo(routeToNavigate?: string): void {
    if (routeToNavigate) {
      this._router.navigateByUrl(routeToNavigate);
      return;
    }
    this._router.navigate([''], { replaceUrl: true });
  }
}
